import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";

/**
 * Admin · Orders & Revenue dashboard.
 * Surfaces:
 *  - KPI cards: total revenue / week / month / paid orders / abandoned / failed
 *  - Paid orders table (revenue)
 *  - Abandoned cart table — Razorpay opened but not paid → one-click WhatsApp recovery
 *  - Failed verifications — payment may have succeeded at bank but verify failed
 */
function fmt(n) { return `₹${Number(n || 0).toLocaleString("en-IN")}`; }
function date(s) { try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s || "-"; } }

function waLink(phone, name, kind) {
  const clean = (phone || "").replace(/[^0-9]/g, "");
  const indian = clean.length === 10 ? `91${clean}` : clean;
  const message = kind === "abandoned"
    ? `Hi ${name || "there"}, we noticed you started publishing your Weddinsite invitation but didn't complete payment. Need help? Reply with any questions — we're here to make it work.`
    : `Hi ${name || "there"}, regarding your Weddinsite payment — if your bank debited the amount but the invitation isn't live, please share your payment ID and we'll activate it within minutes.`;
  return `https://wa.me/${indian}?text=${encodeURIComponent(message)}`;
}

const TabPill = ({ active, onClick, label, count, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${active ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}>
    {label} <span className="ml-2 opacity-70">({count})</span>
  </button>
);

export default function AdminOrders() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("paid");

  const load = () => api.get("/admin/orders").then((r) => setData(r.data)).catch(() => setData({ kpi: {}, paid: [], abandoned: [], failed: [] }));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading orders…</div>;

  const k = data.kpi || {};

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-orders-page">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-16 py-10">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-stone-500 mb-2">Admin · Live Orders</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light">Orders & Revenue</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin" className="px-4 py-2 border border-stone-300 text-xs uppercase tracking-[0.2em] hover:bg-stone-100">← Admin home</Link>
            <button onClick={load} className="px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-[0.2em]" data-testid="orders-refresh">Refresh</button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10" data-testid="orders-kpis">
          {[
            { label: "Total revenue", value: fmt(k.total_revenue), big: true, key: "kpi-total-rev" },
            { label: "This week", value: fmt(k.revenue_this_week), key: "kpi-week-rev" },
            { label: "This month", value: fmt(k.revenue_this_month), key: "kpi-month-rev" },
            { label: "Paid orders", value: k.paid_orders || 0, key: "kpi-paid-count" },
            { label: "Abandoned", value: k.abandoned_carts || 0, accent: "amber", key: "kpi-abandoned" },
            { label: "Failed verify", value: k.failed_verifications || 0, accent: "red", key: "kpi-failed" },
          ].map((c) => (
            <div key={c.label} className={`bg-white border p-5 ${c.big ? "border-emerald-500 ring-1 ring-emerald-200" : "border-stone-200"}`} data-testid={c.key}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500">{c.label}</div>
              <div className={`font-serif-display text-3xl mt-2 ${c.accent === "amber" ? "text-amber-700" : c.accent === "red" ? "text-red-700" : c.big ? "text-emerald-700" : "text-stone-900"}`}>
                {c.value}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <TabPill active={tab === "paid"} onClick={() => setTab("paid")} label="Paid" count={data.paid.length} testid="tab-paid" />
          <TabPill active={tab === "abandoned"} onClick={() => setTab("abandoned")} label="Abandoned" count={data.abandoned.length} testid="tab-abandoned" />
          <TabPill active={tab === "failed"} onClick={() => setTab("failed")} label="Failed verify" count={data.failed.length} testid="tab-failed" />
        </div>

        {/* Paid */}
        {tab === "paid" && (
          <div className="bg-white border border-stone-200 overflow-x-auto" data-testid="paid-orders-table">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-[10px] uppercase tracking-[0.2em]">
                <tr>
                  <th className="text-left p-3">Couple / Project</th>
                  <th className="text-left p-3">Owner</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Paid at</th>
                  <th className="text-left p-3">Public link</th>
                </tr>
              </thead>
              <tbody>
                {data.paid.length === 0 && (
                  <tr><td colSpan="5" className="p-6 text-center text-stone-400">No paid orders yet.</td></tr>
                )}
                {data.paid.map((o) => (
                  <tr key={o.id} className="border-t border-stone-100" data-testid={`paid-row-${o.id}`}>
                    <td className="p-3 font-serif-display">{o.title}</td>
                    <td className="p-3"><div className="text-stone-900">{o.owner_name || "-"}</div><div className="text-xs text-stone-500">{o.owner_email}</div></td>
                    <td className="p-3 font-semibold text-emerald-700">{fmt(o.amount)}</td>
                    <td className="p-3 text-stone-600">{date(o.paid_at)}</td>
                    <td className="p-3"><a href={`/i/${o.slug}`} target="_blank" rel="noreferrer" className="text-emerald-700 underline">/i/{o.slug}</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Abandoned */}
        {tab === "abandoned" && (
          <div className="bg-white border border-amber-300 overflow-x-auto" data-testid="abandoned-orders-table">
            <div className="bg-amber-50 px-4 py-3 text-xs text-amber-900 border-b border-amber-200">
              💡 These customers <strong>opened the Razorpay popup but didn't complete payment</strong>. Reach out on WhatsApp to recover the sale.
            </div>
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-[10px] uppercase tracking-[0.2em]">
                <tr>
                  <th className="text-left p-3">Couple / Project</th>
                  <th className="text-left p-3">Owner</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Started</th>
                  <th className="text-left p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.abandoned.length === 0 && (
                  <tr><td colSpan="5" className="p-6 text-center text-stone-400">No abandoned carts. 🎉</td></tr>
                )}
                {data.abandoned.map((o) => (
                  <tr key={o.id} className="border-t border-stone-100" data-testid={`abandoned-row-${o.id}`}>
                    <td className="p-3 font-serif-display">{o.title}</td>
                    <td className="p-3"><div className="text-stone-900">{o.owner_name || "-"}</div><div className="text-xs text-stone-500">{o.owner_email}</div><div className="text-xs text-stone-500">{o.owner_phone || ""}</div></td>
                    <td className="p-3 font-semibold text-amber-700">{fmt(o.amount)}</td>
                    <td className="p-3 text-stone-600">{date(o.updated_at || o.created_at)}</td>
                    <td className="p-3">
                      {o.owner_phone ? (
                        <a href={waLink(o.owner_phone, o.owner_name, "abandoned")} target="_blank" rel="noreferrer" data-testid={`abandoned-wa-${o.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#25D366] text-white text-[10px] uppercase tracking-[0.2em]">♥ WhatsApp</a>
                      ) : (
                        <span className="text-xs text-stone-400">No phone on file</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Failed */}
        {tab === "failed" && (
          <div className="bg-white border border-red-300 overflow-x-auto" data-testid="failed-orders-table">
            <div className="bg-red-50 px-4 py-3 text-xs text-red-900 border-b border-red-200">
              ⚠️ Payment verification failed. <strong>Bank may have debited the amount</strong> — reach out & manually verify/recover via Razorpay dashboard.
            </div>
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-[10px] uppercase tracking-[0.2em]">
                <tr>
                  <th className="text-left p-3">Owner</th>
                  <th className="text-left p-3">Order ID</th>
                  <th className="text-left p-3">Payment ID</th>
                  <th className="text-left p-3">Error</th>
                  <th className="text-left p-3">When</th>
                </tr>
              </thead>
              <tbody>
                {data.failed.length === 0 && (
                  <tr><td colSpan="5" className="p-6 text-center text-stone-400">No failed verifications.</td></tr>
                )}
                {data.failed.map((f, i) => (
                  <tr key={f.id || i} className="border-t border-stone-100" data-testid={`failed-row-${i}`}>
                    <td className="p-3">{f.owner_email}</td>
                    <td className="p-3 font-mono text-xs">{f.order_id || "-"}</td>
                    <td className="p-3 font-mono text-xs">{f.payment_id || "-"}</td>
                    <td className="p-3 text-xs text-red-700">{f.error || "-"}</td>
                    <td className="p-3 text-stone-600">{date(f.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
